<template>
  <div class="approval-management">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span class="title">审批管理</span>
          <div style="display: flex; gap: 10px;">
            <el-button type="primary" @click="handleSearch">
              <el-icon><Search /></el-icon>
              查询
            </el-button>
            <el-button @click="handleReset">
              重置
            </el-button>
          </div>
        </div>
      </template>

      <!-- 搜索和筛选区域 -->
      <div class="search-section">
        <el-row :gutter="20" align="middle">
          <el-col :span="4">
            <div class="filter-item">
              <label class="filter-label">流程名称</label>
              <el-input
                v-model="queryParams.processName"
                placeholder="请输入流程名称"
                clearable
                @keyup.enter="handleSearch"
                @clear="handleSearch"
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="filter-item">
              <label class="filter-label">业务类型</label>
              <el-select
                v-model="queryParams.businessType"
                placeholder="请选择业务类型"
                clearable
                style="width: 100%"
                @change="handleBusinessTypeChange"
              >
                <el-option label="全部类型" value="" />
                <el-option label="合同" value="CONTRACT" />
                <el-option label="返货单" value="RETURN_ORDER" />
                <el-option label="报销申请" value="TRAVEL_EXPENSE" />
                <el-option label="出差申请" value="BUSINESS_TRIP" />
                <el-option label="成交台账" value="CUSTOMER_DEAL_RECORD" />
              </el-select>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="filter-item">
              <label class="filter-label">提交时间</label>
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="YYYY-MM-DD"
                style="width: 100%"
                @change="handleDateRangeChange"
                clearable
              />
            </div>
          </el-col>
          <el-col :span="10">
            <div class="filter-item">
              <label class="filter-label">审批状态</label>
              <div class="filter-buttons">
                <el-button
                  :type="queryParams.approvalStatus === '' ? 'info' : ''"
                  :plain="queryParams.approvalStatus !== ''"
                  size="default"
                  @click="handleStatusFilter('')"
                >
                  全部状态
                </el-button>
                <el-badge v-if="pendingCount > 0" :value="pendingCount" class="status-badge">
                  <el-button
                    :type="queryParams.approvalStatus === 'PENDING' ? 'warning' : ''"
                    :plain="queryParams.approvalStatus !== 'PENDING'"
                    size="default"
                    @click="handleStatusFilter('PENDING')"
                  >
                    待审批
                  </el-button>
                </el-badge>
                <el-button
                  v-else
                  :type="queryParams.approvalStatus === 'PENDING' ? 'warning' : ''"
                  :plain="queryParams.approvalStatus !== 'PENDING'"
                  size="default"
                  @click="handleStatusFilter('PENDING')"
                >
                  待审批
                </el-button>
                <el-badge v-if="approvingCount > 0" :value="approvingCount" class="status-badge">
                  <el-button
                    :type="queryParams.approvalStatus === 'APPROVING' ? 'primary' : ''"
                    :plain="queryParams.approvalStatus !== 'APPROVING'"
                    size="default"
                    @click="handleStatusFilter('APPROVING')"
                  >
                    审批中
                  </el-button>
                </el-badge>
                <el-button
                  v-else
                  :type="queryParams.approvalStatus === 'APPROVING' ? 'primary' : ''"
                  :plain="queryParams.approvalStatus !== 'APPROVING'"
                  size="default"
                  @click="handleStatusFilter('APPROVING')"
                >
                  审批中
                </el-button>
                <el-button
                  :type="queryParams.approvalStatus === 'APPROVED' ? 'success' : ''"
                  :plain="queryParams.approvalStatus !== 'APPROVED'"
                  size="default"
                  @click="handleStatusFilter('APPROVED')"
                >
                  已通过
                </el-button>
                <el-button
                  :type="queryParams.approvalStatus === 'REJECTED' ? 'danger' : ''"
                  :plain="queryParams.approvalStatus !== 'REJECTED'"
                  size="default"
                  @click="handleStatusFilter('REJECTED')"
                >
                  已驳回
                </el-button>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 审批列表 -->
      <div class="table-container">
        <el-table
          v-loading="loading"
          :data="approvalList"
          stripe
          style="width: 100%"
          table-layout="auto"
          @row-click="handleRowClick"
        >
        <el-table-column prop="businessTitle" label="流程名称" min-width="200" show-overflow-tooltip>
          <template #default="{ row }">
            <div class="title-cell">
              <span class="title-text">{{ row.processName || row.businessTitle || '未命名' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="businessType" label="流程类型" width="120" align="center">
          <template #default="{ row }">
            <el-tag
              :type="getBusinessTypeTagType(row.businessType)"
              size="small"
            >
              {{ getBusinessTypeName(row.businessType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="salesmanName" label="所属业务员" width="120" align="center" />
        <el-table-column prop="createTime" label="提交时间" width="170" align="center">
          <template #default="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="approvalTime" label="审批时间" width="170" align="center">
          <template #default="{ row }">
            {{ row.approvalTime ? formatDateTime(row.approvalTime) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="approvalStatus" label="审批状态" width="120" align="center">
          <template #default="{ row }">
            <!-- 优先使用 nodeStatus（节点状态），如果没有则使用 approvalStatus（流程总状态，兼容旧数据） -->
            <el-tag :type="getStatusTagType(row.nodeStatus || row.approvalStatus)" size="small">
              {{ getStatusName(row.nodeStatus || row.approvalStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="rejectReason" label="驳回原因" min-width="180" align="center" show-overflow-tooltip>
          <template #default="{ row }">
            <span v-if="row.approvalStatus === 'REJECTED' && row.rejectReason" class="reject-reason-text">
              {{ row.rejectReason }}
            </span>
            <span v-else class="reject-reason-empty">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="110" fixed="right" align="center">
          <template #default="{ row }">
            <el-button type="primary" link @click.stop="handleViewDetail(row)">
              查看详情
            </el-button>
          </template>
        </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'InternalApprovalManagement'
}
</script>

<script setup>
import { ref, onMounted, onUnmounted, onActivated } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import {
  getApprovalList
} from '@/api/managerApproval'
import { setupPageCacheRefresh, setPageRefreshTime, shouldRefreshPage } from '../utils/pageCache'

const router = useRouter()
const route = useRoute()

// 数据
const loading = ref(false)
const approvalList = ref([])
const total = ref(0)
const pendingCount = ref(0)
const approvingCount = ref(0)

// 查询参数
const queryParams = ref({
  page: 1,
  pageSize: 10,
  approvalStatus: '',
  businessType: '',
  processName: '',
  startDate: '',
  endDate: ''
})

// 日期范围
const dateRange = ref(null)

// 页面缓存键
const PAGE_CACHE_KEY = 'internal-approval-management-list'

// 清理函数
let cleanupCacheRefresh = null

// 加载审批列表
const loadApprovalList = async () => {
  loading.value = true
  try {
    const res = await getApprovalList(queryParams.value)
    if (res.code === '000000' && res.data) {
      // 直接使用后端返回的列表和总数，避免前端再次过滤导致条数与总数不一致
      approvalList.value = res.data.list || []
      total.value = res.data.total || 0
      // 记录刷新时间
      setPageRefreshTime(PAGE_CACHE_KEY)
      
      // 如果当前查询的是待审批状态，更新待审批数量并通知 Home.vue 更新角标数量
      if (queryParams.value.approvalStatus === 'PENDING') {
        // 直接使用服务器返回的数量，确保是数字类型
        pendingCount.value = Number(res.data.total) || 0
        console.log('待审批列表查询完成，数量:', pendingCount.value)
        // 发送事件通知 Home.vue 更新角标数量
        window.dispatchEvent(new CustomEvent('approval-pending-count-updated', {
          detail: { count: pendingCount.value }
        }))
      }
      // 如果当前查询的是审批中状态，更新审批中数量
      if (queryParams.value.approvalStatus === 'APPROVING') {
        // 直接使用服务器返回的数量，确保是数字类型
        approvingCount.value = Number(res.data.total) || 0
        console.log('审批中列表查询完成，数量:', approvingCount.value)
      }
    } else {
      ElMessage.error(res.msg || '加载失败')
    }
  } catch (error) {
    console.error('加载审批列表失败:', error)
    ElMessage.error('加载失败，请重试')
  } finally {
    loading.value = false
  }
}

// 加载待审批数量（使用和列表相同的查询逻辑）
const loadPendingCount = async (autoSelectPending = false) => {
  try {
    // 使用和列表相同的查询接口，只查询待审批状态，不传递其他筛选条件
    const res = await getApprovalList({
      page: 1,
      pageSize: 1, // 只需要总数，不需要数据
      approvalStatus: 'PENDING' // 只查询待审批状态
    })
    if (res.code === '000000' && res.data) {
      // 取列表返回的 total 值，确保是数字类型
      pendingCount.value = Number(res.data.total) || 0
      
      // 如果待审批数量大于0，且需要自动选中（首次加载且当前没有设置状态筛选）
      if (autoSelectPending && pendingCount.value > 0 && queryParams.value.approvalStatus === '') {
        // 自动选中待审批状态并搜索
        queryParams.value.approvalStatus = 'PENDING'
        loadApprovalList()
      }
    }
  } catch (error) {
    console.error('加载待审批数量失败:', error)
  }
}

// 加载审批中数量（使用和列表相同的查询逻辑）
const loadApprovingCount = async () => {
  try {
    // 使用和列表相同的查询接口，只查询审批中状态，不传递其他筛选条件
    const res = await getApprovalList({
      page: 1,
      pageSize: 1, // 只需要总数，不需要数据
      approvalStatus: 'APPROVING' // 只查询审批中状态
    })
    if (res.code === '000000' && res.data) {
      // 取列表返回的 total 值，确保是数字类型
      approvingCount.value = Number(res.data.total) || 0
    }
  } catch (error) {
    console.error('加载审批中数量失败:', error)
  }
}

// 搜索
const handleSearch = () => {
  queryParams.value.page = 1
  loadApprovalList()
}

// 状态筛选
const handleStatusFilter = (status) => {
  queryParams.value.approvalStatus = status
  queryParams.value.page = 1
  loadApprovalList()
}

// 业务类型下拉框变化
const handleBusinessTypeChange = (value) => {
  queryParams.value.businessType = value || ''
  queryParams.value.page = 1
  loadApprovalList()
}

// 日期范围变化
const handleDateRangeChange = (dates) => {
  if (dates && dates.length === 2) {
    queryParams.value.startDate = dates[0]
    queryParams.value.endDate = dates[1]
  } else {
    queryParams.value.startDate = ''
    queryParams.value.endDate = ''
  }
  queryParams.value.page = 1
  loadApprovalList()
}

// 重置
const handleReset = () => {
  queryParams.value.processName = ''
  queryParams.value.businessType = ''
  queryParams.value.approvalStatus = ''
  queryParams.value.startDate = ''
  queryParams.value.endDate = ''
  queryParams.value.page = 1
  dateRange.value = null
  // 重置后重新查询
  handleSearch()
  // 更新数量
  loadPendingCount(false)
  loadApprovingCount()
}

// 分页
const handleSizeChange = (size) => {
  queryParams.value.pageSize = size
  queryParams.value.page = 1
  loadApprovalList()
}

const handlePageChange = (page) => {
  queryParams.value.page = page
  loadApprovalList()
}

// 行点击
const handleRowClick = (row) => {
  handleViewDetail(row)
}

// 查看详情
const handleViewDetail = (row) => {
  // 记录当前审批列表路径，用于详情页返回时知道应该回到哪个列表页
  try {
    sessionStorage.setItem('lastApprovalListPath', '/home/internal-approval')
  } catch (e) {
    console.warn('保存审批列表路径失败:', e)
  }
  router.push({
    name: 'ApprovalDetail',
    params: { id: row.id },
    // 将当前列表的审批状态一并传给详情页，方便详情页在返回时知道是从
    // “待审批”还是“审批中”等状态进入，从而正确刷新角标数量
    query: {
      previousStatus: queryParams.value.approvalStatus || (row.nodeStatus || row.approvalStatus || '')
    }
  })
}

// 格式化日期时间
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return dateStr
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

// 获取状态名称
const getStatusName = (status) => {
  const names = {
    PENDING: '待审批',
    APPROVING: '审批中',
    WAITING: '等待中',
    APPROVED: '已通过',
    REJECTED: '已驳回'
  }
  return names[status] || status
}

// 获取状态标签类型
const getStatusTagType = (status) => {
  const types = {
    PENDING: 'warning',
    APPROVING: 'primary',
    WAITING: 'info',
    APPROVED: 'success',
    REJECTED: 'danger'
  }
  return types[status] || 'info'
}

// 获取业务类型名称
const getBusinessTypeName = (type) => {
  const names = {
    CONTRACT: '合同',
    RETURN_ORDER: '返货单',
    TRAVEL_EXPENSE: '报销申请',
    BUSINESS_TRIP: '出差申请',
    CUSTOMER_DEAL_RECORD: '成交台账'
  }
  return names[type] || type
}

// 获取业务类型标签类型
const getBusinessTypeTagType = () => {
  return 'info'
}

// 初始化
onMounted(async () => {
  // 检查是否从角标点击进入（通过 query 参数判断）
  const fromBadge = route.query.fromBadge === 'true' || route.query.autoPending === 'true'
  
  // 检查是否从首页点击审批中进入
  const autoApproving = route.query.autoApproving === 'true'
  
  // 检查是否从审批详情返回（通过 query 参数判断）
  const autoStatus = route.query.autoStatus
  const needRefresh = route.query.refresh === 'true'
  
  // 检查是否有直接传递的审批状态
  const directStatus = route.query.approvalStatus
  
  if (fromBadge) {
    // 从角标点击进入，强制设置为待审批状态
    console.log('从角标点击进入，强制设置为待审批状态')
    queryParams.value.approvalStatus = 'PENDING'
    queryParams.value.page = 1
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else if (autoApproving || directStatus === 'APPROVING') {
    // 从首页点击审批中进入，强制设置为审批中状态
    console.log('从首页点击审批中进入，强制设置为审批中状态')
    queryParams.value.approvalStatus = 'APPROVING'
    queryParams.value.page = 1
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else if (directStatus === 'PENDING') {
    // 从首页点击待审批进入，强制设置为待审批状态
    console.log('从首页点击待审批进入，强制设置为待审批状态')
    queryParams.value.approvalStatus = 'PENDING'
    queryParams.value.page = 1
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else if (autoStatus && needRefresh) {
    // 从审批详情返回，根据审批状态自动切换并刷新
    console.log('从审批详情返回，自动切换到状态:', autoStatus)
    
    // 获取审批操作前的状态，用于重新加载角标数量
    const previousStatus = route.query?.previousStatus
    
    // 直接重新加载角标数量（而不是先加载再减少）
    // 因为服务器已经更新了数据，直接重新加载即可获得正确的数量
    try {
      // 如果之前是待审批状态，重新加载待审批数量
      // 注意：即使 autoStatus 不是 PENDING（比如从待审批变为审批中），也要刷新待审批角标
      if (previousStatus === 'PENDING') {
        await loadPendingCount(false)
        // 通知 Home.vue 更新菜单角标数量
        try {
          window.dispatchEvent(new CustomEvent('approval-pending-count-updated', {
            detail: { count: pendingCount.value }
          }))
        } catch (e) {
          console.warn('无法发送事件:', e)
        }
      }
      // 如果之前是审批中状态，重新加载审批中数量
      if (previousStatus === 'APPROVING') {
        await loadApprovingCount()
      }
      // 如果当前状态是审批中，且之前是待审批，也要刷新审批中数量
      // 因为从待审批打开详情后，状态会变为审批中
      if (autoStatus === 'APPROVING' && previousStatus === 'PENDING') {
        await loadApprovingCount()
      }
    } catch (error) {
      console.error('重新加载角标数量时出错:', error)
      // 即使出错也继续执行后续逻辑
    }
    
    queryParams.value.approvalStatus = autoStatus
    queryParams.value.page = 1
    // 立即加载列表
    loadApprovalList()
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else if (needRefresh && route.query?.previousStatus) {
    // 从审批详情返回，但没有 autoStatus（可能是普通返回）
    // 如果有 previousStatus，也要刷新对应的角标
    const previousStatus = route.query.previousStatus
    console.log('从审批详情返回（普通返回），刷新角标，previousStatus:', previousStatus)
    
    try {
      // 如果之前是待审批状态，重新加载待审批数量
      if (previousStatus === 'PENDING') {
        await loadPendingCount(false)
        // 通知 Home.vue 更新菜单角标数量
        try {
          window.dispatchEvent(new CustomEvent('approval-pending-count-updated', {
            detail: { count: pendingCount.value }
          }))
        } catch (e) {
          console.warn('无法发送事件:', e)
        }
      }
      // 如果之前是审批中状态，重新加载审批中数量
      if (previousStatus === 'APPROVING') {
        await loadApprovingCount()
      }
    } catch (error) {
      console.error('重新加载角标数量时出错:', error)
      // 即使出错也继续执行后续逻辑
    }
    
    // 刷新列表
    loadApprovalList()
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else {
    // 先加载待审批数量，如果大于0则自动选中待审批状态
    await loadPendingCount(true)
    // 加载审批中数量
    loadApprovingCount()
    // 加载列表
    loadApprovalList()
  }
  
  // 设置缓存刷新监听
  cleanupCacheRefresh = setupPageCacheRefresh(() => {
    if (!loading.value) {
      loadApprovalList()
      // 刷新时也刷新待审批数量和审批中数量
      loadPendingCount(false)
      loadApprovingCount()
    }
  })
})

onUnmounted(() => {
  // 清理缓存刷新监听
  if (cleanupCacheRefresh) {
    cleanupCacheRefresh()
    cleanupCacheRefresh = null
  }
})

// 当页面从缓存中激活时（keep-alive）
onActivated(async () => {
  // 检查是否从角标点击进入（通过 query 参数判断）
  const fromBadge = route.query.fromBadge === 'true' || route.query.autoPending === 'true'
  
  // 检查是否从首页点击审批中进入
  const autoApproving = route.query.autoApproving === 'true'
  
  // 检查是否从审批详情返回（通过 query 参数判断）
  const autoStatus = route.query.autoStatus
  const needRefresh = route.query.refresh === 'true'
  
  // 检查是否有直接传递的审批状态
  const directStatus = route.query.approvalStatus
  
  if (fromBadge) {
    // 从角标点击进入，强制设置为待审批状态（即使页面缓存存在）
    console.log('从角标点击进入（页面激活），强制设置为待审批状态')
    queryParams.value.approvalStatus = 'PENDING'
    queryParams.value.page = 1
    loadApprovalList()
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else if (autoApproving || directStatus === 'APPROVING') {
    // 从首页点击审批中进入，强制设置为审批中状态（即使页面缓存存在）
    console.log('从首页点击审批中进入（页面激活），强制设置为审批中状态')
    queryParams.value.approvalStatus = 'APPROVING'
    queryParams.value.page = 1
    loadApprovalList()
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else if (directStatus === 'PENDING') {
    // 从首页点击待审批进入，强制设置为待审批状态（即使页面缓存存在）
    console.log('从首页点击待审批进入（页面激活），强制设置为待审批状态')
    queryParams.value.approvalStatus = 'PENDING'
    queryParams.value.page = 1
    loadApprovalList()
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else if (autoStatus && needRefresh) {
    // 从审批详情返回，根据审批状态自动切换并刷新
    console.log('从审批详情返回，自动切换到状态:', autoStatus)
    
    // 获取审批操作前的状态，用于重新加载角标数量
    const previousStatus = route.query?.previousStatus
    
    // 直接重新加载角标数量（而不是先加载再减少）
    // 因为服务器已经更新了数据，直接重新加载即可获得正确的数量
    try {
      // 如果之前是待审批状态，重新加载待审批数量
      if (previousStatus === 'PENDING') {
        await loadPendingCount(false)
        // 通知 Home.vue 更新菜单角标数量
        try {
          window.dispatchEvent(new CustomEvent('approval-pending-count-updated', {
            detail: { count: pendingCount.value }
          }))
        } catch (e) {
          console.warn('无法发送事件:', e)
        }
      }
      // 如果之前是审批中状态，重新加载审批中数量
      if (previousStatus === 'APPROVING') {
        await loadApprovingCount()
      }
    } catch (error) {
      console.error('重新加载角标数量时出错:', error)
      // 即使出错也继续执行后续逻辑
    }
    
    queryParams.value.approvalStatus = autoStatus
    queryParams.value.page = 1
    loadApprovalList()
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else {
    // 页面激活时，如果数据超过2分钟未刷新，则自动刷新
    if (shouldRefreshPage(PAGE_CACHE_KEY, 120000)) {
      console.log('审批管理页面激活：数据已过期，自动刷新')
      loadApprovalList()
      loadPendingCount(false)
      loadApprovingCount()
    }
  }
})
</script>

<style scoped>
.approval-management {
  padding: 20px;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }

    .badge {
      :deep(.el-badge__content) {
        top: 8px;
        right: 8px;
      }
    }
  }

  .search-section {
    margin-bottom: 20px;
    padding: 20px;
    background: #ffffff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    width: 100%;
    box-sizing: border-box;

    .filter-item {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .filter-label {
        color: #606266;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.5;
      }
    }

    .filter-buttons {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      padding-top: 2px;

      .status-badge {
        :deep(.el-badge__content) {
          top: -8px;
          right: -8px;
          font-size: 12px;
          min-width: 18px;
          height: 18px;
          line-height: 18px;
          padding: 0 6px;
        }
      }
    }
  }

  .table-container {
    padding: 20px;
    background: #ffffff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    width: 100%;
    box-sizing: border-box;
    overflow-x: auto;
  }
  
  .table-container :deep(.el-table) {
    width: 100% !important;
    min-width: 100%;
  }
  
  .table-container :deep(.el-table__body-wrapper) {
    width: 100% !important;
  }
  
  .table-container :deep(.el-table__header-wrapper) {
    width: 100% !important;
  }

  .title-cell {
    display: flex;
    align-items: center;

    .title-text {
      font-weight: 500;
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  .reject-reason-text {
    color: #f56c6c;
    font-size: 13px;
  }

  .reject-reason-empty {
    color: #909399;
  }

  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
